/**
 * 图片生成进度管理 Composable
 * 用于管理消息中图片生成的进度状态（0-100%）
 */

import { ref } from 'vue'

export interface ImageProgressMessage {
  id?: string
  msgId?: string
  imageProgress?: number
  isGeneratingImage?: boolean
  state?: number
  image?: string
  video?: string
  coverImage?: string

}

export function useImageProgress() {
  // 每条消息的进度映射（messageId -> progress）
  const progressMap = ref<Record<string, number>>({})
  
  // 每条消息的进度定时器
  const progressTimers = ref<Record<string, NodeJS.Timeout>>({})

  /**
   * 启动进度定时器：在指定时间内从当前进度推进到 99%
   */
  const startProgressTimer = (message: ImageProgressMessage, duration = 10000) => {
    const messageId = message?.id || message?.msgId
    if (!messageId) return

    // 已经有定时器了，不重复创建
    if (progressTimers.value[messageId]) return

    let currentProgress = message.imageProgress ?? progressMap.value[messageId] ?? 0

    // 已经接近完成或已完成，无需再启动定时器
    if (currentProgress >= 99) return

    const interval = 50 // 每 50ms 更新一次
    const steps = duration / interval
    const remaining = 99 - currentProgress
    const increment = remaining / steps

    progressTimers.value[messageId] = setInterval(() => {
      currentProgress += increment
      if (currentProgress >= 99) {
        currentProgress = 99
        clearProgressTimer(messageId)
      }
      const value = Math.floor(currentProgress)
      progressMap.value[messageId] = value
      // 同步到消息对象上
      message.imageProgress = value
    }, interval)
  }

  /**
   * 清除进度定时器
   */
  const clearProgressTimer = (messageId: string) => {
    if (progressTimers.value[messageId]) {
      clearInterval(progressTimers.value[messageId])
      delete progressTimers.value[messageId]
    }
  }

  /**
   * 获取图片生成进度
   */
  const getProgress = (message: ImageProgressMessage): number => {
    const messageId = message?.id || message?.msgId
    if (!messageId) return 0

    const savedProgress = message.imageProgress

    // 优先使用消息对象上的进度
    if (savedProgress !== undefined) {
      progressMap.value[messageId] = savedProgress

      // 0~99：生图进行中，确保有定时器继续推进
      if (savedProgress >= 0 && savedProgress < 99 && message.isGeneratingImage) {
        startProgressTimer(message)
      }

      // 100：已完成
      if (savedProgress === 100) {
        clearProgressTimer(messageId)
        return 100
      }

      return savedProgress
    }

    // 如果消息已经有图片且 state === 30，视为 100%
    if (message.image && message.state === 30) {
      if (progressMap.value[messageId] !== 100) {
        progressMap.value[messageId] = 100
        clearProgressTimer(messageId)
      }
      return 100
    }

    return progressMap.value[messageId] || 0
  }

  /**
   * 初始化图片生成进度（从 0% 开始）
   */
  const initProgress = (message: ImageProgressMessage, duration = 10000) => {
    const messageId = message?.id || message?.msgId
    if (!messageId) return

    // 初始化进度为 0
    progressMap.value[messageId] = 0
    message.imageProgress = 0

    // 清除之前的定时器
    clearProgressTimer(messageId)

    // 启动新的进度定时器
    startProgressTimer(message, duration)
  }

  /**
   * 完成图片生成进度（设为 100%）
   */
  const completeProgress = (message: ImageProgressMessage) => {
    const messageId = message?.id || message?.msgId
    if (!messageId) return

    clearProgressTimer(messageId)
    progressMap.value[messageId] = 100
    message.imageProgress = 100

    // 短暂展示 100% 后重置为 0
    setTimeout(() => {
      progressMap.value[messageId] = 0
      if (message.imageProgress !== undefined) {
        delete message.imageProgress
      }
    }, 500)
  }

  /**
   * 重置图片生成进度（清除状态）
   */
  const resetProgress = (message: ImageProgressMessage) => {
    const messageId = message?.id || message?.msgId
    if (!messageId) return

    clearProgressTimer(messageId)
    delete progressMap.value[messageId]
    if (message.imageProgress !== undefined) {
      delete message.imageProgress
    }
  }

  /**
   * 清理所有定时器（组件卸载时调用）
   */
  const cleanup = () => {
    Object.values(progressTimers.value).forEach(timer => {
      if (timer) clearInterval(timer)
    })
    progressTimers.value = {}
    progressMap.value = {}
  }

  return {
    progressMap,
    getProgress,
    initProgress,
    completeProgress,
    resetProgress,
    clearProgressTimer,
    cleanup
  }
}

